import { faroCoreApiClient } from "@api/faro-core-api-client";
import {
  BaseCompanyProjectIdProps,
  BaseProjectIdProps,
  ProjectId,
} from "@custom-types/base-types";
import { ProjectSettings } from "@custom-types/project-types";
import { SlideContainerMarkupSettings } from "@faro-lotv/ielement-types";
import { createMutationSetElementMetaData } from "@faro-lotv/service-wires";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import {
  EditProjectFeatureProps,
  EditProjectFeatureResult,
  FetchProjectsByIdsProps,
  FetchProjectsProps,
  RemoveMemberProps,
  RemoveMemberResult,
  UpdateMemberRoleInProject,
  UpdateProjectDetailsProps,
  UpdateProjectSettingsProps,
} from "@store/projects/projects-slice-types";
import { updateMemberRoleInProjectBase } from "@store/projects/projects-slice-utils";
import { RootState } from "@store/store-helper";
import { BaseProjectApiClientProps } from "@store/store-types";
import {
  isProjectDemo,
  projectStateToApiArchivingState,
} from "@utils/project-utils";

// TODO: No need to pass companyId as createAsyncThunk can access the store: https://faro01.atlassian.net/browse/HBD-253
/**
 * Fetches projects queried by the passed params.
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchProjects = createAsyncThunk<
  FaroCoreApiTypes.GetProjectsResponse,
  FetchProjectsProps
>(
  "projects/fetchProjects",
  async ({
    companyId,
    projectArchivingState,
    next,
    search,
    groupId,
    sortBy,
  }) => {
    return await faroCoreApiClient.projects.getProjects({
      companyId,
      archivingStates: projectStateToApiArchivingState(projectArchivingState),
      start: next,
      search,
      groupId,
      sortBy,
    });
  }
);

/**
 * Fetch project detail by providing projectId from the backend
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchProjectDetails = createAsyncThunk<
  FaroCoreApiTypes.ProjectDetails,
  BaseCompanyProjectIdProps
>("projects/fetchProjectDetails", async ({ companyId, projectId }) => {
  return await faroCoreApiClient.projects.getProjectDetails({
    companyId,
    projectId,
  });
});


/**
 * Update project details
 * @throws {Error} if no companyId or projectId exist to update the project details
 * or if the project is a demo project
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const updateProjectDetails = createAsyncThunk<
  FaroCoreApiTypes.ProjectDetails,
  UpdateProjectDetailsProps,
  {
    state: RootState;
  }
>(
  "projects/updateProjectDetails",
  async ({ projectId, payload }, { getState }) => {
    const {
      projects,
      projects: { selectedProject },
      sdbCompany: { selectedSdbCompanyId },
    } = getState();

    /**
     * The ID of the project that is going to be updated.
     * If projectId is not provided, the selectedProjectId from store is selected
     */
    const updatingProjectId = projectId || selectedProject.id;

    if (!selectedSdbCompanyId || !updatingProjectId) {
      throw new Error(
        "No companyId or projectId exist to was given to updateProjectDetails"
      );
    }

    if (
      selectedProject.id &&
      isProjectDemo(projects.entities[selectedProject.id])
    ) {
      throw new Error("Demo project can't be edited");
    }

    const updatedProject =
      await faroCoreApiClient.projects.updateProjectDetails({
        companyId: selectedSdbCompanyId,
        projectId: updatingProjectId,
        payload,
      });

    return updatedProject;
  }
);

/**
 * Remove a member from the project using the backend,
 * and then removes it from project in the store as well
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const removeMemberFromProject = createAsyncThunk<
  RemoveMemberResult,
  RemoveMemberProps
>(
  "projects/removeMemberFromProject",
  async ({ companyId, projectId, member }) => {
    await faroCoreApiClient.projects.removeMemberFromProject({
      companyId,
      projectId,
      userIdentity: member.identity,
    });

    return {
      projectId,
      member,
    };
  }
);

/**
 * Send request to backend to edit project feature,
 * and then update it in the store as well
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const editProjectFeature = createAsyncThunk<
  EditProjectFeatureResult,
  EditProjectFeatureProps
>(
  "projects/editProjectFeature",
  async ({ companyId, selectedProject, payload }) => {
    const newFeatures = await faroCoreApiClient.projects.setProjectFeatures({
      companyId,
      projectId: selectedProject.id,
      payload,
    });

    return { newFeatures, selectedProject };
  }
);

/**
 * Fetch project context for a project from backend, and then update it in the store as well
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchProjectContext = createAsyncThunk<
  FaroCoreApiTypes.GetProjectContext,
  BaseProjectIdProps
>("projects/fetchProjectContext", async ({ projectId }) => {
  return await faroCoreApiClient.projects.getProjectContext({
    projectId,
  });
});

/**
 * Delete a project from the backend
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const deleteProject = createAsyncThunk<
  ProjectId,
  BaseCompanyProjectIdProps
>("projects/deleteProject", async ({ companyId, projectId }) => {
  await faroCoreApiClient.projects.deleteProject({ companyId, projectId });
  return projectId;
});

/**
 * Update a member role in the project using the backend,
 * and then update it in project in the store as well.
 * Intended to use when updating a single member role,
 * since the error will be automatically handled by the error slice.
 */
export const updateMemberRoleInProjectSingle = createAsyncThunk<
  UpdateMemberRoleInProject,
  FaroCoreApiTypes.UpdateMemberRoleInProjectProps
>("projects/updateMemberRoleInProjectSingle", updateMemberRoleInProjectBase);

/**
 * Update a member role in the project using the backend,
 * and then update it in project in the store as well.
 * Intended to use when updating a member roles in bulk,
 * since the error won't be automatically handled by the error slice.
 */
export const updateMemberRoleInProjectBulk = createAsyncThunk<
  UpdateMemberRoleInProject,
  FaroCoreApiTypes.UpdateMemberRoleInProjectProps
>("projects/updateMemberRoleInProjectBulk", updateMemberRoleInProjectBase);

/**
 * Fetch project settings for a project from the Project API backend.
 * The settings are included in the "metaDataMap" property of root ielement of the project.
 * @throws {ApiResponseError} if the Project API client request fails
 */
export const fetchProjectSettings = createAsyncThunk<
  ProjectSettings,
  BaseProjectApiClientProps
>("projects/fetchProjectSettings", async ({ projectApiClient }) => {
  const rootElement = await projectApiClient.getRootIElement();

  const shouldDisplayMarkupsForViewers =
    rootElement.metaDataMap?.slideContainerMarkupSettings?.displayForViewers;

  return {
    shouldDisplayMarkupsForViewers,
  };
});

/**
 * Updates the project settings for the current project
 * @returns the update project settings
 * @throws {ApiResponseError} if the Project API client request fails
 */
export const updateProjectSettings = createAsyncThunk<
  ProjectSettings,
  UpdateProjectSettingsProps
>("projects/updateProjectSettings", async ({ projectApiClient, payload }) => {
  // Fetch the root IElement of the project. The ID of the root is required to update the settings
  const { id } = await projectApiClient.getRootIElement();

  const setting: SlideContainerMarkupSettings = {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by Project Api backend
    displayForViewers: payload.shouldDisplayMarkupsForViewers,
  };
  const mutation = createMutationSetElementMetaData(id, [
    {
      key: "slideContainerMarkupSettings",
      value: setting,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by Project Api backend
      skipIfPresent: false,
    },
  ]);

  // Update "displayForViewers" value of the root IElement
  await projectApiClient.applyMutations([mutation]);

  // Fetch the root IElement again to get the updated setting value
  const root = await projectApiClient.getRootIElement();

  const shouldDisplayMarkupsForViewers =
    root.metaDataMap?.slideContainerMarkupSettings?.displayForViewers;
  return {
    shouldDisplayMarkupsForViewers,
  };
});

/**
 * Fetch projects by providing projectIds from the backend
 * @throws {Error} if there is no selected companyId in the store
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchProjectsByIds = createAsyncThunk<
  FaroCoreApiTypes.ProjectDetails[],
  FetchProjectsByIdsProps,
  { state: RootState }
>("projects/fetchProjectsByIds", async ({ projectIds }, { getState }) => {
  const {
    sdbCompany: { selectedSdbCompanyId },
  } = getState();

  if (!selectedSdbCompanyId) {
    throw new Error(
      "No companyId or projectId exist to was given to updateProjectDetails"
    );
  }

  const fetchedProjects = await Promise.allSettled(
    [...new Set(projectIds)].map((projectId) =>
      faroCoreApiClient.projects.getProjectDetails({
        companyId: selectedSdbCompanyId,
        projectId,
      })
    )
  );

  // Extract only fulfilled values
  const successfulProjects = fetchedProjects
    .filter((result) => result.status === "fulfilled")
    .map((result) => result.value);

  return successfulProjects;
});
